import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { MDTypography } from "../../../../Component/Common/Components/MDTypography";
import { ImportManagement } from "../../../../Component/Model/Dto/VerificationAgency/Client/ImportManagement/ImportManagement";
import { Card, CardContent, Grid } from "@mui/material";
import { ImportProgressBar } from "../../../../Component/Common/Components/ProgressBar/ImportProgressBar";
import {
    VerificationWorkflowService
} from "../../../../Component/Api/VerificationAgency/VerificationWorkflow/VerificationWorkflowService";
import { BBBEEScorecardAtom } from "../../../../State/BBBEEScorecard.Atom";
import { OrganisationAtom } from "../../../../State/Organisation.Atom";
import { useRecoilValue } from "recoil";
import { toast } from "react-toastify";
import {useSignalRConnection} from "../../../../Hooks/useSignalRConnection";


export const ImportDetails = () => {
    const { importDetailsId, useSignalR, scorecardId } = useParams() as {
        importDetailsId: string;
        useSignalR: string;
        scorecardId?: string;
    };
    const location = useLocation();

    const [importDetails, setImportDetails] = useState<ImportManagement.ImportDetailDto>();
    const [progressData, setProgressData] = useState<{ [key: string]: ImportManagement.ImportDetailSheetDto }>();
    const [useSignalRValue] = useState<boolean>(useSignalR === "true");
    const scorecard = useRecoilValue(BBBEEScorecardAtom);
    const organisation = useRecoilValue(OrganisationAtom);
    const hub = useSignalRConnection("ImportHub");

    const isPayrollImport = location.pathname.includes('/organisation/payrollData/details');

    const fetchData = async () => {
        try {
            const result = await VerificationWorkflowService.GetImportDetails(parseInt(importDetailsId));
            setImportDetails(result.data);
            result.data?.progressSheets?.map(x =>
                {
                    setProgressData((prevData) => ({
                        ...prevData,
                        [x.importTypeId]: {...x}
                    }));
                }
            );

        } catch (error) {
            console.log(error);
        } 
    }

    useEffect(() => {
        fetchData();
     // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        if(!useSignalRValue) return;

        (async () => {
            try {
                if(!hub.isConnected)
                {
                    await hub.start();
                }
                const eventNames = isPayrollImport
                    ? {
                        progress: 'ImportInProgress',
                        failed: 'ImportFailed',
                        success: 'ImportSuccess',
                        complete: 'ImportComplete'
                    }
                    : {
                        progress: 'excelImportProgress',
                        failed: 'excelImportFailed',
                        success: 'excelImportSuccess',
                        complete: 'excelImportComplete'
                    };

                const identifier = isPayrollImport
                    ? `payroll-${scorecardId}-${organisation.OrganisationId}-${importDetailsId}`
                    : `${scorecard.scorecardId}-${importDetailsId}`;

                const completionIdentifier = isPayrollImport
                    ? `payroll-${scorecardId}`
                    : identifier;

                hub.on(`${eventNames.progress}-${identifier}`, (data: any) => {
                    const { ImportTypeId, progress } = data;
                    setProgressData((prevData) => ({
                        ...prevData,
                        [ImportTypeId]: {
                            ...prevData![ImportTypeId],
                            progressPercentage: progress
                        }
                    }));
                });

                hub.on(`${eventNames.failed}-${identifier}`, (data: any) => {
                    const { ImportTypeId, NumberOfRowsCorrect, NumberOfRowsFailed, Log, ErrorBlobUrl } = data;
                    setProgressData((prevData) => ({
                        ...prevData,
                        [ImportTypeId]: {
                            ...prevData![ImportTypeId],
                            progressPercentage: 0,
                            importSuccessful: false,
                            numberOfRowsCorrect: NumberOfRowsCorrect,
                            numberOfRowsFailed: NumberOfRowsFailed,
                            log: Log,
                            errorBlobUrl: ErrorBlobUrl
                        }
                    }));
                });

                hub.on(`${eventNames.success}-${identifier}`, (data: any) => {
                    const { ImportTypeId, NumberOfRowsCorrect, NumberOfRowsFailed, Log } = data;
                    setProgressData((prevData) => ({
                        ...prevData,
                        [ImportTypeId]: {
                            ...prevData![ImportTypeId],
                            progressPercentage: 100,
                            importSuccessful: true,
                            numberOfRowsCorrect: NumberOfRowsCorrect,
                            numberOfRowsFailed: NumberOfRowsFailed,
                            log: Log
                        }
                    }));
                });

                hub.on(`${eventNames.complete}-${completionIdentifier}`, () => {
                    const message = isPayrollImport
                        ? "All payroll imports have been completed successfully"
                        : "All Excel sheets have been imported successfully";
                    toast.success(message);
                });
            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();
    }, []);
    
    return (
        <Grid container >
            <Grid item xs={12} lg={12}>
                <MDTypography variant='h4' mb={3}>Imports for {importDetails?.scorecardName}</MDTypography>
            </Grid>
            <Grid item xs={12} lg={12}>
            {progressData && Object.entries(progressData).map(([key, sheet]) => {
                return (
                        <Card key={sheet.importTypeId} variant="outlined" sx={{marginBottom: "15px"}}>
                        <CardContent>
                            <MDTypography variant="h5" mb={2} mt={2}>{sheet.dataPageName}</MDTypography>
                            <ImportProgressBar
                                value={sheet.progressPercentage ?? 0}
                                progressData={{
                                    progress: sheet.progressPercentage,
                                    numberOfRowsCorrect: sheet.numberOfRowsCorrect,
                                    numberOfRowsFailed: sheet.numberOfRowsFailed,
                                    log:  ((sheet.importSuccessful && sheet.numberOfRowsFailed === 0)? [] : [""]),
                                    isSuccess: sheet.importSuccessful,
                                    errorLogUrl: sheet.errorBlobUrl,
                                    lastSuccessfulStep: sheet.lastSuccessfulStep
                                }}
                                errorLogUrl={sheet.errorBlobUrl}
                                isExcelImport={true}
                            />
                        </CardContent>
                    </Card>
                    )
                })
            }
        </Grid>
    </Grid>
    )
}
